/* ==========================================================================
   CSV table
   A <csv-table> custom element that fetches a CSV file and renders it with
   DataTables 2.x.

   Loading strategy
     1. Nothing happens until the element is ~300px from the viewport.
     2. jQuery is skipped if the theme already ships it.
     3. Vendor files come from one CDN origin so the connection is reused.
     4. The parsed result is cached in sessionStorage for the configured TTL.

   To self-host, upload the three vendor files to assets/ and swap the URLs
   in VENDOR below for {{ 'file.js' | asset_url }} values.
   ========================================================================== */

(function () {
  'use strict';

  var VENDOR = {
    jquery: 'https://cdn.jsdelivr.net/npm/jquery@3.7.1/dist/jquery.min.js',
    datatables: 'https://cdn.jsdelivr.net/npm/datatables.net@2.1.8/js/dataTables.min.js',
    responsive: 'https://cdn.jsdelivr.net/npm/datatables.net-responsive@3.0.3/js/dataTables.responsive.min.js'
  };

  // Must match the min-width used by .csvt--fit in csv-table.css.
  var FIT_QUERY = '(min-width: 990px)';

  var CACHE_PREFIX = 'csvt:v1:';
  var MAX_CACHE_BYTES = 1500000;

  /* Header colourways, cycled across the columns in order. */
  var PALETTES = {
    vivid: ['#0b1a6b', '#e01b24', '#4b2ee0', '#171717', '#1c9c3e', '#c0392b',
            '#e08a1e', '#8b6ce8', '#e2756f', '#2b2b2b', '#4a322c', '#e02b20'],
    jewel: ['#123f5e', '#7b2d4e', '#1f6f5c', '#5c3b8a', '#8a5a1f', '#2b4b7d',
            '#6d2f2f', '#1d6b74', '#4a4a1f', '#7a2f5e', '#2f5e3a', '#5e2f2f'],
    earth: ['#3f3a2f', '#7a6a4f', '#5a6650', '#8a6f4b', '#4a5b63', '#6b4f3f',
            '#7d7a5a', '#55604a', '#8a7a68', '#4f4a42', '#6a5b4a', '#3d4a44'],
    slate: ['#1f2933', '#323f4b', '#3e4c59', '#52606d', '#616e7c', '#1f2933',
            '#323f4b', '#3e4c59', '#52606d', '#616e7c', '#1f2933', '#323f4b']
  };

  function contrastText(hex) {
    var m = /^#?([a-f\d]{3}|[a-f\d]{6})$/i.exec(String(hex || '').trim());
    if (!m) return null;
    var h = m[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    var channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(function (c) {
      c /= 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    var luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
    return luminance > 0.45 ? '#1c1c1c' : '#ffffff';
  }

  function isSetColor(value) {
    var v = String(value || '').trim().toLowerCase();
    return v !== '' && v !== 'rgba(0,0,0,0)' && v !== 'transparent' && v !== 'none';
  }

  /* --- Script loading --------------------------------------------------- */

  var loaded = {};

  function loadScript(src) {
    if (loaded[src]) return loaded[src];
    loaded[src] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = function () {
        delete loaded[src];
        reject(new Error('Could not load ' + src));
      };
      document.head.appendChild(s);
    });
    return loaded[src];
  }

  function hasJquery() {
    return !!(window.jQuery && window.jQuery.fn && parseFloat(window.jQuery.fn.jquery) >= 1.8);
  }

  function loadVendor(needResponsive) {
    var chain = hasJquery() ? Promise.resolve() : loadScript(VENDOR.jquery);

    chain = chain.then(function () {
      return window.DataTable ? null : loadScript(VENDOR.datatables);
    });

    if (needResponsive) {
      chain = chain.then(function () {
        var DT = window.DataTable;
        return DT && DT.Responsive ? null : loadScript(VENDOR.responsive);
      });
    }

    return chain.then(function () {
      var DT = window.DataTable || (window.jQuery && window.jQuery.fn.dataTable);
      if (!DT) throw new Error('DataTables did not initialise');
      return DT;
    });
  }

  /* --- CSV parsing (RFC 4180: quotes, escaped quotes, embedded newlines) - */

  function detectDelimiter(sample) {
    var candidates = [',', ';', '\t', '|'];
    var best = ',';
    var bestCount = 0;
    var firstLine = sample.split(/\r?\n/)[0] || '';

    candidates.forEach(function (d) {
      var count = 0;
      var inQuotes = false;
      for (var i = 0; i < firstLine.length; i++) {
        var c = firstLine[i];
        if (c === '"') inQuotes = !inQuotes;
        else if (c === d && !inQuotes) count++;
      }
      if (count > bestCount) {
        bestCount = count;
        best = d;
      }
    });

    return best;
  }

  function parseCSV(text, delimiter) {
    if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

    var rows = [];
    var row = [];
    var field = '';
    var inQuotes = false;
    var i = 0;
    var len = text.length;

    function pushField() {
      row.push(field);
      field = '';
    }

    function pushRow() {
      pushField();
      var empty = row.every(function (v) { return v === ''; });
      if (!empty) rows.push(row);
      row = [];
    }

    while (i < len) {
      var c = text[i];

      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
          inQuotes = false; i++; continue;
        }
        field += c; i++; continue;
      }

      if (c === '"' && field === '') { inQuotes = true; i++; continue; }
      if (c === delimiter) { pushField(); i++; continue; }
      if (c === '\r') { i++; continue; }
      if (c === '\n') { pushRow(); i++; continue; }

      field += c;
      i++;
    }

    if (field !== '' || row.length) pushRow();
    return rows;
  }

  /* --- Value helpers ---------------------------------------------------- */

  var NUMERIC_RE = /^-?[\d.,\s]*\d([eE][-+]?\d+)?$/;

  function toNumber(value) {
    if (value == null) return NaN;
    var s = String(value).replace(/[^\d.,\-eE+]/g, '').trim();
    if (!s) return NaN;

    var lastComma = s.lastIndexOf(',');
    var lastDot = s.lastIndexOf('.');

    if (lastComma > -1 && lastDot > -1) {
      s = lastComma > lastDot
        ? s.replace(/\./g, '').replace(',', '.')
        : s.replace(/,/g, '');
    } else if (lastComma > -1) {
      // A single comma is a decimal mark only when it is not a group separator.
      s = /,\d{3}$/.test(s) ? s.replace(/,/g, '') : s.replace(',', '.');
    }

    var n = parseFloat(s);
    return isNaN(n) ? NaN : n;
  }

  function looksNumeric(rows, index) {
    var seen = 0;
    var numeric = 0;
    var limit = Math.min(rows.length, 60);

    for (var i = 0; i < limit; i++) {
      var raw = (rows[i][index] || '').trim();
      if (!raw) continue;
      seen++;
      if (NUMERIC_RE.test(raw.replace(/[$€£₹%\s]/g, '')) && !isNaN(toNumber(raw))) numeric++;
    }

    return seen > 0 && numeric / seen >= 0.8;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function safeUrl(value) {
    var v = String(value || '').trim();
    return /^(https?:)?\/\//i.test(v) || v.charAt(0) === '/' ? v : '';
  }

  /* --- Column building -------------------------------------------------- */

  function findRule(rules, header, index) {
    for (var i = 0; i < rules.length; i++) {
      var key = (rules[i].key || '').trim();
      if (!key) continue;
      if (/^\d+$/.test(key) && parseInt(key, 10) - 1 === index) return rules[i];
      if (key.toLowerCase() === String(header).trim().toLowerCase()) return rules[i];
    }
    return null;
  }

  function makeRenderer(rule, cfg) {
    var format = (rule && rule.format) || 'text';
    var prefix = (rule && rule.prefix) || '';
    var suffix = (rule && rule.suffix) || '';
    var decimals = rule && typeof rule.decimals === 'number' ? rule.decimals : 2;
    var locale = cfg.locale || undefined;

    if (format === 'text') {
      return function (data, type) {
        if (type !== 'display') return data;
        var text = escapeHtml(data);
        return text ? escapeHtml(prefix) + text + escapeHtml(suffix) : '';
      };
    }

    if (format === 'number' || format === 'currency' || format === 'percent') {
      return function (data, type) {
        var n = toNumber(data);
        if (type === 'sort' || type === 'type') return isNaN(n) ? null : n;
        if (type !== 'display') return data;
        if (isNaN(n)) return escapeHtml(data);

        var opts = { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
        if (format === 'currency') {
          opts.style = 'currency';
          opts.currency = cfg.currency || 'USD';
        }

        var out;
        try {
          out = new Intl.NumberFormat(locale, opts).format(format === 'percent' ? n : n);
        } catch (e) {
          out = n.toFixed(decimals);
        }

        if (format === 'percent') out += '%';
        return escapeHtml(prefix) + escapeHtml(out) + escapeHtml(suffix);
      };
    }

    if (format === 'date') {
      return function (data, type) {
        var t = Date.parse(data);
        if (type === 'sort' || type === 'type') return isNaN(t) ? null : t;
        if (type !== 'display') return data;
        if (isNaN(t)) return escapeHtml(data);
        try {
          return new Intl.DateTimeFormat(locale, {
            year: 'numeric', month: 'short', day: 'numeric'
          }).format(new Date(t));
        } catch (e) {
          return escapeHtml(data);
        }
      };
    }

    if (format === 'link') {
      var target = rule && rule.link_target
        ? ' target="_blank" rel="noopener noreferrer"'
        : '';
      return function (data, type) {
        if (type !== 'display') return data;
        var parts = String(data || '').split('|');
        var href = safeUrl(parts.length > 1 ? parts[1] : parts[0]);
        var label = (parts[0] || '').trim() || href;
        if (!href) return escapeHtml(data);
        return '<a class="csvt__cell-link" href="' + escapeHtml(href) + '"' + target + '>' +
          escapeHtml(label) + '</a>';
      };
    }

    if (format === 'image') {
      return function (data, type) {
        if (type !== 'display') return data;
        var src = safeUrl(data);
        if (!src) return '';
        return '<img class="csvt__cell-img" loading="lazy" decoding="async" ' +
          'src="' + escapeHtml(src) + '" alt="">';
      };
    }

    return function (data, type) {
      if (type !== 'display') return data;
      var text = escapeHtml(data);
      return text ? '<span class="csvt__badge">' + text + '</span>' : '';
    };
  }

  function buildColumns(headers, rows, cfg) {
    var rules = cfg.columns || [];

    return headers.map(function (header, index) {
      console.log("header>>>>>",header);
      var rule = findRule(rules, header, index);
      var format = (rule && rule.format) || 'text';
      var numeric = format === 'number' || format === 'currency' || format === 'percent' ||
        (format === 'text' && looksNumeric(rows, index));

      var align = rule && rule.align && rule.align !== 'auto'
        ? rule.align
        : (numeric ? 'end' : 'start');

      var label = escapeHtml((rule && rule.label) || header || 'Column ' + (index + 1));
      var href = rule ? safeUrl(rule.header_url) : '';
      if (href) {
        label = '<a class="csvt__head-link" href="' + escapeHtml(href) + '"' +
          (rule.header_new_tab ? ' target="_blank" rel="noopener noreferrer"' : '') +
          '>' + label + '</a>';
      }

      var column = {
        title: label,
        data: index,
        defaultContent: '',
        className: 'csvt-' + align,
        orderable: rule ? rule.sortable !== false : true,
        visible: !(rule && rule.hidden),
        render: makeRenderer(rule, cfg)
      };

      if (rule && rule.width) column.width = rule.width;
      if (format === 'image' || format === 'badge') column.orderable = false;

      return column;
    });
  }

  function headerColors(headers, cfg) {
    var rules = cfg.columns || [];
    var palette = PALETTES[cfg.headerColorway] || null;

    return headers.map(function (header, index) {
      var rule = findRule(rules, header, index);
      var bg = rule && isSetColor(rule.header_bg) ? rule.header_bg : null;
      if (!bg && palette) bg = palette[index % palette.length];
      if (!bg) return null;

      var fg = rule && isSetColor(rule.header_text)
        ? rule.header_text
        : contrastText(bg);

      return { bg: bg, fg: fg };
    });
  }

  function bindHeaderLinks(tableEl) {
    var links = tableEl.querySelectorAll('thead a.csvt__head-link');

    for (var i = 0; i < links.length; i++) {
      links[i].addEventListener('click', function (event) {
        // The whole header cell is a sort control. The link is deeper in the
        // tree, so stopping here runs before the delegated sort handler.
        event.stopPropagation();
      });
    }
  }

  function applyHeaderColors(tableEl, columns, heads) {
    var cells = tableEl.querySelectorAll('thead > tr > th');
    var visible = 0;

    for (var i = 0; i < columns.length; i++) {
      if (columns[i].visible === false) continue;
      var cell = cells[visible++];
      var head = heads[i];
      if (!cell || !head) continue;
      cell.style.backgroundColor = head.bg;
      if (head.fg) cell.style.color = head.fg;
    }
  }

  function resolveInitialSort(spec, headers, columns) {
    if (!spec) return [];
    var parts = String(spec).trim().split(/\s+/);
    var dir = (parts[parts.length - 1] || '').toLowerCase();
    var hasDir = dir === 'asc' || dir === 'desc';
    var name = (hasDir ? parts.slice(0, -1).join(' ') : spec).trim().toLowerCase();
    if (!name) return [];

    for (var i = 0; i < headers.length; i++) {
      var title = String(headers[i]).trim().toLowerCase();
      var label = String(columns[i].title).trim().toLowerCase();
      if (title === name || label === name) {
        return [[i, hasDir ? dir : 'asc']];
      }
    }
    return [];
  }

  /* --- Cache ------------------------------------------------------------ */

  function readCache(key, minutes) {
    if (!minutes) return null;
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (Date.now() - parsed.t > minutes * 60000) {
        sessionStorage.removeItem(CACHE_PREFIX + key);
        return null;
      }
      return parsed.d;
    } catch (e) {
      return null;
    }
  }

  function writeCache(key, data, minutes) {
    if (!minutes) return;
    try {
      var payload = JSON.stringify({ t: Date.now(), d: data });
      if (payload.length > MAX_CACHE_BYTES) return;
      sessionStorage.setItem(CACHE_PREFIX + key, payload);
    } catch (e) {
      /* Quota full or private mode — the table still works without cache. */
    }
  }

  /* --- The element ------------------------------------------------------ */

  class CsvTable extends HTMLElement {}

  CsvTable.prototype.connectedCallback = function () {
    if (this.started) return;

    var configEl = document.querySelector(this.getAttribute('data-config'));
    if (!configEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (e) {
      this.showError('The section settings could not be read.', e.message);
      return;
    }

    this.loadingEl = this.querySelector('[data-csvt-loading]');
    this.errorEl = this.querySelector('[data-csvt-error]');
    this.scrollEl = this.querySelector('[data-csvt-scroll]');
    this.tableEl = this.querySelector('[data-csvt-table]');

    var retry = this.querySelector('[data-csvt-retry]');
    if (retry) retry.addEventListener('click', this.start.bind(this));

    if (this.config.designMode || !('IntersectionObserver' in window)) {
      this.start();
      return;
    }

    var self = this;
    this.observer = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      self.observer.disconnect();
      self.observer = null;
      self.start();
    }, { rootMargin: '300px 0px' });

    this.observer.observe(this);
  };

  CsvTable.prototype.disconnectedCallback = function () {
    if (this.observer) this.observer.disconnect();
    this.destroy();
  };

  CsvTable.prototype.destroy = function () {
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
      clearTimeout(this.resizeTimer);
      this.onResize = null;
    }
    if (this.mq && this.onBreakpoint) {
      if (this.mq.removeEventListener) {
        this.mq.removeEventListener('change', this.onBreakpoint);
      } else {
        this.mq.removeListener(this.onBreakpoint);
      }
      this.mq = null;
      this.onBreakpoint = null;
    }
    this.teardownTable();
    this.started = false;
  };

  CsvTable.prototype.start = function () {
    if (this.started) return;
    this.started = true;

    var self = this;
    var cfg = this.config;
    var needResponsive = cfg.responsiveMode === 'collapse';

    this.setState('loading');

    Promise.all([this.getRows(), loadVendor(needResponsive)])
      .then(function (results) {
        self.render(results[0], results[1]);
      })
      .catch(function (error) {
        self.started = false;
        self.showError('The table data could not be loaded.', error.message || '');
      });
  };

  CsvTable.prototype.getRows = function () {
    var cfg = this.config;

    if (cfg.inlineId) {
      var el = document.getElementById(cfg.inlineId);
      var text = el ? el.textContent : '';
      if (!text.trim()) return Promise.reject(new Error('The pasted CSV is empty.'));
      return Promise.resolve(this.toRows(text));
    }

    if (!cfg.url) {
      return Promise.reject(new Error('Add a CSV file URL in the section settings.'));
    }

    var key = cfg.url + '|' + cfg.delimiter;
    var minutes = cfg.designMode ? 0 : cfg.cacheMinutes;
    var cached = readCache(key, minutes);
    if (cached) return Promise.resolve(cached);

    var self = this;
    return fetch(cfg.url, { credentials: 'omit' })
      .then(function (response) {
        if (!response.ok) throw new Error('The file returned ' + response.status + '.');
        return response.text();
      })
      .then(function (text) {
        var parsed = self.toRows(text);
        writeCache(key, parsed, minutes);
        return parsed;
      });
  };

  CsvTable.prototype.toRows = function (text) {
    var cfg = this.config;
    var delimiter = cfg.delimiter === 'auto' ? detectDelimiter(text.slice(0, 4000)) : cfg.delimiter;
    var rows = parseCSV(text, delimiter);

    if (!rows.length) throw new Error('The file has no rows.');

    var headers;
    if (cfg.firstRowHeader) {
      headers = rows.shift().map(function (h, i) { return h.trim() || 'Column ' + (i + 1); });
    } else {
      headers = rows[0].map(function (_, i) { return 'Column ' + (i + 1); });
    }

    // Pad short rows so every row matches the header count.
    rows = rows.map(function (row) {
      while (row.length < headers.length) row.push('');
      return row.slice(0, headers.length);
    });

    return { headers: headers, rows: rows };
  };

  CsvTable.prototype.render = function (data, DT) {
    var cfg = this.config;
    var columns = buildColumns(data.headers, data.rows, cfg);
    var searchFirst = cfg.controlsLayout !== 'length_start';

    this.data = data;
    this.DT = DT;

    // Above the fit breakpoint every column is guaranteed to fit, so the
    // Responsive extension is switched off rather than left to measure a
    // fixed-layout table and hide a column that was never overflowing.
    var wide = window.matchMedia(FIT_QUERY).matches;
    var fitting = cfg.fitColumns && wide;
    var collapse = cfg.responsiveMode === 'collapse' && !fitting;

    // How many numbered buttons the pager shows before it starts eliding.
    // This lives on the extension defaults, so it has to be set before init.
    if (DT.ext && DT.ext.pager) {
      DT.ext.pager.numbers_length = cfg.pageNumbers || 7;
    }

    var options = {
      data: data.rows,
      columns: columns,
      deferRender: true,
      autoWidth: false,
      order: resolveInitialSort(cfg.initialSort, data.headers, columns),
      ordering: cfg.ordering,
      paging: cfg.paging,
      pagingType: cfg.pagingType || 'numbers',
      pageLength: cfg.pageLength || 25,
      lengthMenu: [10, 25, 50, 100],
      searching: cfg.searching,
      searchDelay: 250,
      info: cfg.info,
      responsive: collapse ? { details: { type: 'inline' } } : false,
      layout: {
        topStart: searchFirst
          ? (cfg.searching ? 'search' : null)
          : (cfg.paging && cfg.lengthMenu ? 'pageLength' : null),
        topEnd: searchFirst
          ? (cfg.paging && cfg.lengthMenu ? 'pageLength' : null)
          : (cfg.searching ? 'search' : null),
        bottomStart: cfg.info ? 'info' : null,
        bottomEnd: cfg.paging ? 'paging' : null
      },
      language: {
        search: cfg.searchLabel || '',
        searchPlaceholder: cfg.searchPlaceholder,
        lengthMenu: cfg.lengthLabel || 'Show _MENU_ rows',
        info: cfg.infoLabel || 'Showing _START_ to _END_ of _TOTAL_ records',
        infoEmpty: 'Showing 0 records',
        infoFiltered: ' (filtered from _MAX_)',
        zeroRecords: cfg.emptyText,
        emptyTable: cfg.emptyText,
        paginate: {
          first: 'First',
          previous: 'Previous',
          next: 'Next',
          last: 'Last'
        }
      }
    };

    // The table must be visible BEFORE DataTables initialises. Its pager
    // measures button heights after each draw and recursively drops two
    // buttons whenever it thinks they have wrapped — inside a hidden
    // container every measurement is zero, so it strips itself down to
    // "… 92" and never recovers.
    if (this.loadingEl) this.loadingEl.hidden = true;
    if (this.errorEl) this.errorEl.hidden = true;
    if (this.scrollEl) this.scrollEl.hidden = false;

    this.table = new DT(this.tableEl, options);

    var container = this.querySelector('.dt-container');
    var tableRow = container && container.querySelector('.dt-layout-table');

    // Only become a scroll box when there is something to scroll — an
    // unnecessary scroll container would break the sticky header.
    if (tableRow && (cfg.maxHeight > 0 || (!collapse && !fitting))) {
      var cell = tableRow.querySelector('.dt-layout-cell') || tableRow;
      cell.classList.add('is-scrollable');
    }

    // Lift the notice out of its placeholder and sit it under the controls bar.
    var notice = this.querySelector('[data-csvt-notice]');
    if (notice && tableRow) {
      notice.hidden = false;
      tableRow.parentNode.insertBefore(notice, tableRow);
    }

    applyHeaderColors(this.tableEl, columns, headerColors(data.headers, cfg));
    bindHeaderLinks(this.tableEl);

    this.bindViewport();
    this.setState('ready');
  };

  CsvTable.prototype.bindViewport = function () {
    if (this.onResize) return;
    var self = this;

    // Re-fit the pager when the width changes — the button count is decided
    // at draw time, so a resize needs a redraw to recalculate it.
    this.onResize = function () {
      clearTimeout(self.resizeTimer);
      self.resizeTimer = setTimeout(function () {
        if (self.table) self.table.draw(false);
      }, 200);
    };
    window.addEventListener('resize', this.onResize);

    // Crossing the fit breakpoint changes whether Responsive should run at
    // all, and that is decided at init, so the table is rebuilt from the
    // already-parsed rows. No refetch, no reparse.
    this.mq = window.matchMedia(FIT_QUERY);
    this.onBreakpoint = function () {
      if (!self.table || !self.data) return;
      var page = self.table.page();
      self.teardownTable();
      self.render(self.data, self.DT);
      if (page) self.table.page(page).draw('page');
    };

    if (this.mq.addEventListener) {
      this.mq.addEventListener('change', this.onBreakpoint);
    } else {
      this.mq.addListener(this.onBreakpoint);
    }
  };

  CsvTable.prototype.teardownTable = function () {
    var notice = this.querySelector('[data-csvt-notice]');
    if (notice) {
      notice.hidden = true;
      this.appendChild(notice);
    }
    if (this.table) {
      this.table.destroy();
      this.table = null;
    }
    var caption = this.tableEl ? this.tableEl.querySelector('caption') : null;
    if (this.tableEl) {
      this.tableEl.innerHTML = '';
      if (caption) this.tableEl.appendChild(caption);
    }
  };

  CsvTable.prototype.setState = function (state) {
    if (this.loadingEl) this.loadingEl.hidden = state !== 'loading';
    if (this.errorEl) this.errorEl.hidden = state !== 'error';
    if (this.scrollEl) this.scrollEl.hidden = state !== 'ready';
  };

  CsvTable.prototype.showError = function (title, body) {
    if (!this.errorEl) return;
    var titleEl = this.errorEl.querySelector('[data-csvt-error-title]');
    var bodyEl = this.errorEl.querySelector('[data-csvt-error-body]');
    if (titleEl) titleEl.textContent = title;
    if (bodyEl) bodyEl.textContent = body || '';
    this.setState('error');
  };

  if (!customElements.get('csv-table')) {
    customElements.define('csv-table', CsvTable);
  }

  /* --- Theme editor lifecycle ------------------------------------------- */

  document.addEventListener('shopify:section:unload', function (event) {
    var el = event.target.querySelector('csv-table');
    if (el && el.destroy) el.destroy();
  });
})();
